import { useEffect, useState } from "react";
import { useAtom } from "lib/global-context";
import { cn } from "lib/utils";
import { ageAtom, ageEndAtom } from "lib/atoms";
import { Input } from "components/ui/input";
import { debounce } from "lodash";
import styles from "./input-age-end.module.css";

interface InputAgeEndProps {
  config: {
    min: number;
    max: number;
    displayConfiguration: {
      inputType: string;
      title: string;
      placeholder?: string;
      name: string;
    };
  };
}

const InputAgeEnd = ({ config }: InputAgeEndProps) => {
  const { min, max, displayConfiguration } = config;
  const { inputType, title, name, placeholder } = displayConfiguration;

  const [startAge] = useAtom(ageAtom);
  const [ageEnd, setAgeEnd] = useAtom(ageEndAtom);
  const [tempValue, setTempValue] = useState<string>(
    ageEnd !== null ? ageEnd.toString() : "",
  );
  const [error, setError] = useState<string | null>(null);

  const validateAgeEnd = debounce((value: string) => {
    const ageEndValue = value === "" ? null : Number(value);
    setAgeEnd(ageEndValue);

    let minAgeEnd = startAge !== null ? startAge + 1 : min;
    minAgeEnd = minAgeEnd < min ? min : minAgeEnd;
    minAgeEnd = minAgeEnd > max ? max : minAgeEnd;
    const maxAgeEnd = max;

    if (ageEndValue === null) {
      setError(`Input an age between ${minAgeEnd} - ${maxAgeEnd}`);
      return;
    }

    if (ageEndValue < minAgeEnd || ageEndValue > maxAgeEnd) {
      setError(`Input an age between ${minAgeEnd} - ${maxAgeEnd}`);
    } else {
      setError(null);
    }
  }, 300);

  useEffect(() => {
    if (/^[0-9]+$/.test(tempValue)) validateAgeEnd(tempValue);
    // Reason for including startAge in the dependency array:
    // Update the validation message when the current age changes
  }, [startAge, tempValue, validateAgeEnd]);

  const handleAgeEndChange = (e: React.ChangeEvent<HTMLInputElement>) =>
    setTempValue(e.target.value);

  return (
    <div className={styles.container}>
      <label
        htmlFor={name}
        className={cn(styles.label, error && styles.labelError)}
      >
        {error || title}
      </label>
      <Input
        id={name}
        type={inputType}
        placeholder={placeholder}
        value={tempValue}
        onChange={handleAgeEndChange}
        className={cn(styles.input, error && styles.inputError)}
        required
      />
    </div>
  );
};

export default InputAgeEnd;
